import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useVinVisibility } from "../admin/VinDecoderGate";
import { 
  BarChart3, Users, FileSignature, BookOpen, Cpu, 
  UserCog, LogOut, Menu, X, ChevronDown,
  Globe, Globe2, Settings, Wrench, Shield, Layers, Cloud, FileCheck, MailOpen, Activity, Radio, Palette, Lock, MessageSquare
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { LOJACK_LOGO, API } from "../../config";
import { SlaBreachBadge } from "../admin/SlaBreachBadge";
import axios from "axios";

const NavDropdown = ({ label, icon: Icon, items, location, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const isActive = items.some(i => location.pathname === i.path || (i.startsWith && location.pathname.startsWith(i.path)));

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const visibleItems = items.filter(i => {
    if (i.adminOnly && onNavigate.role !== 'super_admin') return false;
    if (i.adminOrAbove && !['super_admin', 'admin'].includes(onNavigate.role)) return false;
    if (i.condition !== undefined && !i.condition) return false;
    return true;
  });

  if (visibleItems.length === 0) return null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`nav-link flex items-center gap-1 ${isActive ? 'active' : ''}`}
        data-testid={`nav-dropdown-${label.toLowerCase().replace(/\s/g, '-')}`}
      >
        <Icon className="w-4 h-4" />
        <span>{label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-52 bg-[#1A1A1A] border border-gray-700 rounded-xl shadow-2xl overflow-hidden z-50">
          {visibleItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => { setOpen(false); if (onNavigate.close) onNavigate.close(); }}
              className={`flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${
                location.pathname === item.path || (item.startsWith && location.pathname.startsWith(item.path))
                  ? 'text-lojack-red bg-lojack-red/5'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/60'
              }`}
              data-testid={item.testId}
            >
              <item.icon className="w-4 h-4 flex-shrink-0" />
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export const Navigation = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDocsOpen, setMobileDocsOpen] = useState(false);
  const [mobileGestOpen, setMobileGestOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const { user, logout } = useAuth();
  const vinVis = useVinVisibility(["admin", "super_admin"].includes(user?.role));
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate('/login'); };

  const docsItems = [
    // Zorzutti CR 29/07/2026 — removed "Documenti", "Manuali" and
    // "Info Tecniche" entries as requested (kept "Doc. Legali",
    // "Contratti Legal Preview", "Sicurezza", "UI Style Reference").
    { path: '/admin/legal', label: 'Doc. Legali', icon: FileCheck, testId: 'nav-legal-docs' },
    { path: '/admin/legal-preview', label: 'Contratti Legal Preview', icon: FileCheck, testId: 'nav-legal-preview', adminOrAbove: true },
    { path: '/admin/operational-manuals', label: 'Manuali Operativi', icon: BookOpen, testId: 'nav-operational-manuals', adminOrAbove: true },
    { path: '/admin/security', label: 'Sicurezza', icon: Shield, testId: 'nav-security' },
    { path: '/admin/style-reference', label: 'UI Style Reference', icon: Palette, testId: 'nav-style-reference' },
    { path: '/admin/private-docs', label: 'Documenti riservati', icon: Lock, testId: 'nav-private-docs', condition: !!user?.can_view_private_docs },
  ];

  const gestItems = [
    { path: '/admin/dealers', label: 'Dealer', icon: Wrench, testId: 'nav-dealers' },
    { path: '/admin/dealer-performance', label: 'Performance Dealer', icon: BarChart3, testId: 'nav-dealer-perf' },
    { path: '/admin/users', label: 'Utenti', icon: UserCog, testId: 'nav-users', adminOnly: true },
    { path: '/centers', label: 'Centrali', icon: Globe, testId: 'nav-centers' },
  ];

  const toolsItems = [
    { path: '/admin/salesforce', label: 'Salesforce CRM', icon: Cloud, testId: 'nav-salesforce' },
    { path: '/admin/email-preview', label: 'Email Preview', icon: MailOpen, testId: 'nav-email-preview', adminOrAbove: true },
    { path: '/admin/i18n-status', label: 'Stato i18n', icon: Globe2, testId: 'nav-i18n-status', adminOrAbove: true },
    { path: '/admin/communications', label: 'Comunicazioni', icon: Radio, testId: 'nav-admin-communications', adminOrAbove: true },
    { path: '/admin/sms-credit', label: 'Credito SMS', icon: MessageSquare, testId: 'nav-sms-credit', adminOrAbove: true },
    { path: '/admin/vin-decoder', label: 'VIN Decoder Pro', icon: Activity, testId: 'nav-vin-decoder-pro', adminOrAbove: true, condition: !!vinVis?.visible },
    { path: '/admin/system-health', label: 'Fix Center', icon: Wrench, testId: 'nav-fix-center', adminOrAbove: true },
  ];

  return (
    <nav className="bg-lojack-dark border-b border-gray-800 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-3">
              <img src={LOJACK_LOGO} alt="LoJack Tracker" className="h-9" />
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            <Link to="/admin" className={`nav-link ${location.pathname === '/admin' ? 'active' : ''}`} data-testid="nav-admin">
              <BarChart3 className="inline-block w-4 h-4 mr-1.5" />Dashboard
            </Link>
            <Link to="/admin/customers" className={`nav-link ${location.pathname === '/admin/customers' ? 'active' : ''}`} data-testid="nav-customers">
              <Users className="inline-block w-4 h-4 mr-1.5" />Clienti
            </Link>
            <Link to="/admin/contracts" className={`nav-link ${location.pathname === '/admin/contracts' ? 'active' : ''}`} data-testid="nav-contracts">
              <FileSignature className="inline-block w-4 h-4 mr-1.5" />Contratti
            </Link>

            <NavDropdown label="Documentazione" icon={BookOpen} items={docsItems} location={location} onNavigate={{ role: user?.role }} />
            <NavDropdown label="Gestione" icon={Settings} items={gestItems} location={location} onNavigate={{ role: user?.role }} />
            <NavDropdown label="Strumenti" icon={Layers} items={toolsItems} location={location} onNavigate={{ role: user?.role }} />
            
            <div className="ml-3 pl-3 border-l border-gray-700 flex items-center gap-3">
              <SlaBreachBadge />
              <span className="text-gray-400 text-sm">{user?.name}</span>
              <button onClick={handleLogout} className="p-2 text-gray-400 hover:text-lojack-red transition-colors" title="Logout" data-testid="logout-btn">
                <LogOut size={18} />
              </button>
            </div>
          </div>

          <button className="md:hidden p-2 text-gray-400 hover:text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-800">
            <div className="flex flex-col gap-1">
              <Link to="/admin" className={`nav-link ${location.pathname === '/admin' ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                <BarChart3 className="inline-block w-4 h-4 mr-2" />Dashboard
              </Link>
              <Link to="/admin/customers" className={`nav-link ${location.pathname === '/admin/customers' ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                <Users className="inline-block w-4 h-4 mr-2" />Clienti
              </Link>
              <Link to="/admin/contracts" className={`nav-link ${location.pathname === '/admin/contracts' ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                <FileSignature className="inline-block w-4 h-4 mr-2" />Contratti
              </Link>

              {/* Mobile: Documentazione */}
              <button onClick={() => setMobileDocsOpen(!mobileDocsOpen)} className="nav-link flex items-center justify-between w-full">
                <span className="flex items-center"><BookOpen className="inline-block w-4 h-4 mr-2" />Documentazione</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileDocsOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileDocsOpen && (
                <div className="pl-6 flex flex-col gap-1">
                  {docsItems.filter(i => i.condition === undefined || i.condition).map(item => (
                    <Link key={item.path} to={item.path} className={`nav-link ${location.pathname === item.path ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                      <item.icon className="inline-block w-4 h-4 mr-2" />{item.label}
                    </Link>
                  ))}
                </div>
              )}

              {/* Mobile: Gestione */}
              <button onClick={() => setMobileGestOpen(!mobileGestOpen)} className="nav-link flex items-center justify-between w-full">
                <span className="flex items-center"><Settings className="inline-block w-4 h-4 mr-2" />Gestione</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileGestOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileGestOpen && (
                <div className="pl-6 flex flex-col gap-1">
                  {gestItems.filter(i => !i.adminOnly || user?.role === 'super_admin').map(item => (
                    <Link key={item.path} to={item.path} className={`nav-link ${location.pathname === item.path ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                      <item.icon className="inline-block w-4 h-4 mr-2" />{item.label}
                    </Link>
                  ))}
                </div>
              )}

              {/* Mobile: Strumenti */}
              <button onClick={() => setMobileToolsOpen(!mobileToolsOpen)} className="nav-link flex items-center justify-between w-full">
                <span className="flex items-center"><Layers className="inline-block w-4 h-4 mr-2" />Strumenti</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileToolsOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileToolsOpen && (
                <div className="pl-6 flex flex-col gap-1">
                  {toolsItems
                    .filter(i => i.condition === undefined || i.condition)
                    .filter(i => !i.adminOrAbove || ['super_admin', 'admin'].includes(user?.role))
                    .filter(i => !i.adminOnly || user?.role === 'super_admin')
                    .map(item => (
                    <Link key={item.path} to={item.path} className={`nav-link ${location.pathname === item.path ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)} data-testid={`${item.testId}-mobile`}>
                      <item.icon className="inline-block w-4 h-4 mr-2" />{item.label}
                    </Link>
                  ))}
                </div>
              )}

              <div className="pt-4 mt-4 border-t border-gray-700">
                <div className="mb-3">
                  <SlaBreachBadge />
                </div>
                <div className="text-gray-400 text-sm mb-2">{user?.name}</div>
                <button onClick={handleLogout} className="nav-link text-red-400 flex items-center gap-2">
                  <LogOut size={16} />Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
